/**
 * Which portfolio a property - and so a project - belongs to.
 *
 * The portfolio is set on the property, by the Construction Manager on the
 * Properties page (migration 018). A project has no portfolio of its own: it
 * stores its property's name as text in `asset_name`, and its portfolio is
 * whatever that property's is. Resolved here, once, so the Reports page and
 * the Dashboard cannot answer the question two different ways.
 *
 * The codes are what the database stores and CK_property_portfolio accepts;
 * the labels are what people see. api/test/property-entity.test.ts reads the
 * codes out of this file and fails if they drift from the API's list.
 */

/** Code -> label, in the order the dropdowns show them. */
export const PORTFOLIO_LABELS = {
  non_fund: 'Non-Fund',
  fund: 'Fund',
  hilgard: 'Hilgard',
  individual: 'Individual',
};

/**
 * A property with no portfolio chosen, and a project whose property cannot be
 * found. Not a stored value - the column is NULL - but a filter option, so an
 * unsorted property is something you can look for rather than something that
 * silently drops out of every filtered report.
 */
export const PORTFOLIO_NOT_SET = 'not_set';
export const PORTFOLIO_NOT_SET_LABEL = 'Not set';

export const PORTFOLIO_FILTER_ALL = 'all';

/** The report and Dashboard dropdown's options. */
export const PORTFOLIO_FILTER_OPTIONS = [
  { value: PORTFOLIO_FILTER_ALL, label: 'All Portfolios' },
  ...Object.entries(PORTFOLIO_LABELS).map(([value, label]) => ({ value, label })),
  { value: PORTFOLIO_NOT_SET, label: PORTFOLIO_NOT_SET_LABEL },
];

/** The label for a stored value, or "Not set" for a blank or unknown one. */
export function portfolioLabel(code) {
  return PORTFOLIO_LABELS[code] ?? PORTFOLIO_NOT_SET_LABEL;
}

const nameKey = name => (name ?? '').trim().toLowerCase();

/**
 * A lookup from property name to portfolio code.
 *
 * Case-insensitive, like UX_property_name: the database will not hold
 * "Village Plaza" and "village plaza" as two properties, so a project naming
 * either is on the same one. Retired properties are included - a project that
 * ran on a sold property still belongs to that property's portfolio.
 */
export function portfolioByPropertyName(properties) {
  const map = new Map();
  for (const property of properties ?? []) {
    if (property?.name) map.set(nameKey(property.name), property.portfolio || null);
  }
  return map;
}

/**
 * The portfolio code of a project, or PORTFOLIO_NOT_SET.
 *
 * Not set covers two cases on purpose: the property exists but nobody has
 * chosen its portfolio yet, and the project names a property that is not on
 * the list at all (an old name, a typo). Neither can be guessed.
 */
export function projectPortfolio(project, byName) {
  const code = byName?.get(nameKey(project?.asset_name));
  return code && PORTFOLIO_LABELS[code] ? code : PORTFOLIO_NOT_SET;
}

/** Narrow a list of projects to one portfolio. `all` returns the list as given. */
export function filterProjectsByPortfolio(projects, byName, filter) {
  if (!filter || filter === PORTFOLIO_FILTER_ALL) return projects || [];
  return (projects || []).filter(p => projectPortfolio(p, byName) === filter);
}

/**
 * Read a portfolio typed into a CSV cell.
 *
 * Accepts the code or the label, ignoring case and spacing, because a person
 * editing the export in Excel will type "Non-Fund" or "non fund" as often as
 * "non_fund". Returns the code, null for a blank cell, or undefined for
 * something that is not a portfolio at all.
 */
export function parsePortfolio(raw) {
  const text = (raw ?? '').trim();
  if (!text) return null;
  const squashed = text.toLowerCase().replace(/[\s_-]+/g, '');
  if (squashed === 'notset') return null;
  for (const [code, label] of Object.entries(PORTFOLIO_LABELS)) {
    if (squashed === code.replace(/_/g, '') || squashed === label.toLowerCase().replace(/[\s_-]+/g, '')) {
      return code;
    }
  }
  return undefined;
}
